import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import type { ILandingEventTimePoint, ILandingEventTypeCounts } from '@/entities/landing.entity';
import { LANDING_EVENT_TYPE_LABELS } from '@/entities/landing.entity';
import { BarChart3Icon } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

type SeriesKey = keyof ILandingEventTypeCounts;

interface LandingEventsChartProps {
	data: ILandingEventTimePoint[];
	series?: SeriesKey[];
	title?: string;
}

const chartConfig: Record<SeriesKey, { label: string; color: string }> = {
	connect: { label: LANDING_EVENT_TYPE_LABELS.connect, color: 'var(--chart-1)' },
	download: { label: LANDING_EVENT_TYPE_LABELS.download, color: 'var(--chart-2)' },
	form_submit: { label: LANDING_EVENT_TYPE_LABELS.form_submit, color: 'var(--chart-3)' },
	telegram_auth_start: { label: LANDING_EVENT_TYPE_LABELS.telegram_auth_start, color: 'var(--chart-4)' },
	telegram_auth_code: { label: LANDING_EVENT_TYPE_LABELS.telegram_auth_code, color: 'var(--chart-5)' },
	telegram_auth_password: {
		label: LANDING_EVENT_TYPE_LABELS.telegram_auth_password,
		color: 'var(--chart-1)',
	},
	telegram_auth_session: {
		label: LANDING_EVENT_TYPE_LABELS.telegram_auth_session,
		color: 'var(--chart-2)',
	},
};

const DEFAULT_SERIES: SeriesKey[] = [
	'connect',
	'download',
	'form_submit',
	'telegram_auth_start',
	'telegram_auth_code',
	'telegram_auth_password',
	'telegram_auth_session',
];

function pointHasData(d: ILandingEventTimePoint, keys: SeriesKey[]): boolean {
	return keys.some(key => d[key] > 0);
}

export default function LandingEventsChart({
	data,
	series = DEFAULT_SERIES,
	title = 'События по времени',
}: LandingEventsChartProps) {
	const hasData = data.some(d => pointHasData(d, series));
	const filteredConfig = Object.fromEntries(series.map(key => [key, chartConfig[key]])) as Record<
		string,
		{ label: string; color: string }
	>;

	return (
		<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
			<CardHeader className='border-b px-4 py-3'>
				<div className='flex items-center gap-2'>
					<div className='rounded-lg bg-chart-1/15 p-1.5'>
						<BarChart3Icon className='text-chart-1' size={16} />
					</div>
					<CardTitle className='text-sm'>{title}</CardTitle>
				</div>
			</CardHeader>
			<CardContent className='px-2 py-3'>
				{!hasData ? (
					<div className='h-[180px] flex items-center justify-center text-sm text-muted-foreground'>Нет данных</div>
				) : (
					<ChartContainer config={filteredConfig} className='h-[180px] w-full'>
						<AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
							<CartesianGrid strokeDasharray='3 3' vertical={false} />
							<XAxis dataKey='label' tickLine={false} axisLine={false} tick={{ fontSize: 10 }} interval='preserveStartEnd' />
							<YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
							<ChartTooltip content={<ChartTooltipContent />} />
							{series.map(key => (
								<Area
									key={key}
									type='monotone'
									dataKey={key}
									stackId='events'
									stroke={chartConfig[key].color}
									fill={chartConfig[key].color}
									fillOpacity={0.35}
									strokeWidth={1.5}
								/>
							))}
						</AreaChart>
					</ChartContainer>
				)}
			</CardContent>
		</Card>
	);
}
